import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import BreadCrumsHeader from "../BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Checkbox from "../../common/Checkbox";
import Status from "../Status";
import Delete from "../Delete";
import Edit from "../Edit";
import Pagination from "../TablePagination";
import CreateNewButton from "../CreateNewButton";
import closeIcon from "../../../Assets/Icons/popupCloseIcon.svg";
import whiteTick from "../../../Assets/Icons/White Tick.svg";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedWhiteBox.svg";
import { ReactComponent as UncheckedGreyIcon } from "../../../Assets/Icons/unCheckedGreyIcon.svg";
import { ReactComponent as Image } from "../../../Assets/Icons/Image.svg";
import { ReactComponent as LocationIcon } from "../../../Assets/Icons/Location.svg";
import { ReactComponent as ApprovedSubCategoryIcon } from "../../../Assets/Icons/approvedsubCategoryIcon.svg";
import { ReactComponent as SubCategoryIcon } from "../../../Assets/Icons/subCategoryIcon.svg";
import { ReactComponent as UnapprovedSubCatIcon } from "../../../Assets/Icons/unapprovedSubCatIcon.svg";
import { ReactComponent as ApprovedRulesIcon } from "../../../Assets/Icons/approvedRulesIcon.svg";
import { ReactComponent as RulesIcon } from "../../../Assets/Icons/rulesIcon.svg";
import { ReactComponent as UnapprovedRulesIcon } from "../../../Assets/Icons/unapprovedRulesIcon.svg";
import descriptionIcon from "../../../Assets/Icons/descriptionIcon.svg";
import {
  deleteOffer,
  getNonDraftedOffers,
  getOfferById,
  getOffersByMerchant,
  toggleOfferStatus,
  approveOrRejectResources,
} from "../../../services/offerService";
import ImagePopup from "../ImagePopup";
import LocationPopup from "../LocationPopup";
import CancelPopup from "../CancelPopup";
import deleteImg from "../../../Assets/Images/deleteImg.svg";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import Loader from "../../common/Loader";
import DetailsPopup from "../DetailsPopup";
import SubCategoryPopup from "../SubCategoryPopup";
import Confirmation from "./Confirmation";
import infoIcon from "../../../Assets/Icons/infoIcon.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import SearchBar from "../SearchBar";
import debounce from "lodash.debounce";
import { Dropdown } from "antd";
import FilterPanel from "../FilterPanel";
import { getCategories } from "../../../services/offerService";
import DetailsModal from "../DetailsModal";
import PopupContent from "./PopupContent";
import "./offerList.scss";

const AdminOfferlist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const [allOffers, setAllOffers] = useState(null);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [clickedOffer, setClickedOffer] = useState(null);
  const [reason, setReason] = useState("");
  const [infoClicked, setInfoClicked] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [offerDetails, setOfferDetails] = useState(null);
  const [statusToggled, setStatusToggled] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [showSingleApprovalConfirm, setShowSingleApprovalConfirm] =
    useState(false);
  const [singleApprovalOfferId, setSingleApprovalOfferId] = useState(null);

  const [filterParams, setFilterParams] = useState({
    category: null,
    subCategory: [], // Initialize as empty array for multi-select
    status: null,
    duration: null,
    offerType: null,
    redemptionMethod: null,
  });

  // Persist filter selections across refreshes
  const STORAGE_KEY = "ADMIN_OFFERS_LIST_FILTERS_V1";

  useEffect(() => {
    getCategoriesData();
  }, []);


  // Add these mappings
  const DURATION_MAP = {
    Day: "DAY",
    Week: "WEEK",
    Month: "MONTH",
    Year: "YEAR",
  };

  const STATUS_MAP = {
    Approved: "approved",
    Declined: "declined",
    Pending: "pending",
    // Drafted is intentionally excluded for admin view
  };

  const OFFER_TYPE_MAP = {
    "Instore Swoodle Offer": "INSTORE_SWOODLE_OFFER", // Corrected as per your API
    Online: "ONLINE_OFFER", // Corrected as per your API
  };

  const REDEMPTION_METHOD_MAP = {
    "Swoodle Code Offer": "SWOODLE_CODE_OFFER", // Based on common redemption types matching your API's IN_STORE
    "QR Code Offer": "QR_CODE", // Assuming QR/Bar Code are in-store methods
    "Bar Code Offer": "BAR_CODE", // Assuming QR/Bar Code are in-store methods
  };
  const [filterData, setFilterData] = useState([
    {
      id: "duration",
      title: "Duration",
      options: ["Day", "Week", "Month", "Year"],
      defaultValue: "Day",
      multiSelect: false,
      valueMap: DURATION_MAP, // Add this
    },
    {
      id: "status",
      title: "Status",
      options: ["Approved", "Declined", "Pending"],
      multiSelect: false,
      valueMap: STATUS_MAP,
    },
    {
      id: "category",
      title: "Category",
      options: [], // Will be populated from API
      defaultValue: "",
      multiSelect: false,
      optionValueKey: "_id",
      optionLabelKey: "title",
    },
    {
      id: "subcategory",
      title: "Subcategory",
      options: [], // Will be populated based on selected category
      defaultValue: [],
      multiSelect: true, // Allow multiple subcategory selection
      optionValueKey: "_id",
      optionLabelKey: "title",
    },
    {
      id: "offerType",
      title: "Offer Type",
      options: ["Instore Swoodle Offer", "Online"],
      // defaultValue: "Instore Swoodle Offer",
      multiSelect: false,
      valueMap: OFFER_TYPE_MAP, // Add this
    },
    {
      id: "redemptionMethod",
      title: "Redemption Method",
      options: ["Swoodle Code Offer", "QR Code Offer", "Bar Code Offer"], // Re-evaluate these for mapping to IN_STORE, ONLINE, BOTH
      // defaultValue: "Swoodle Code Offer",
      multiSelect: false,
      valueMap: REDEMPTION_METHOD_MAP, // Add this
    },
  ]);

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        // For multi-select, initialize as array
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        // For single select, initialize as single value
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  // Initialize selections with the initial state
  const [selections, setSelections] = useState(createInitialState(filterData));

  // Rehydrate saved filters on mount
  useEffect(() => {
    try {
      const savedRaw = localStorage.getItem(STORAGE_KEY);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        if (saved?.selections) {
          setSelections((prev) => ({ ...prev, ...saved.selections }));
        }
        if (saved?.filterParams) {
          setFilterParams((prev) => ({ ...prev, ...saved.filterParams }));
        }
      }
    } catch (_) { }
  }, []);

  // Save filters whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ selections, filterParams })
      );
    } catch (_) { }
  }, [selections, filterParams]);

  // Update the getCategoriesData function to populate filterData
  const getCategoriesData = async () => {
    const categoryPromise = await dispatch(getCategories())?.unwrap();
    if (categoryPromise?.success === true) {
      const categoriesData = categoryPromise?.data;
      setCategories(categoriesData);

      // Update the category filter with data from API
      setFilterData((prevFilters) => {
        return prevFilters.map((filter) => {
          if (filter.id === "category") {
            return {
              ...filter,
              options: categoriesData,
              // defaultValue:
              //   categoriesData.length > 0 ? categoriesData[0]._id : "",
            };
          }
          return filter;
        });
      });

      // Update selections with the first category as default if available
      if (categoriesData.length > 0) {
        // setSelections((prev) => ({
        //   ...prev,
        //   // category: categoriesData[0]._id,
        // }));

        // Also update subcategories based on the first category
        updateSubcategoriesForCategory(categoriesData[0]);
      }
    }
  };

  // Helper function to filter approved subcategories
  const getApprovedSubcategories = (category) => {
    if (
      !category ||
      !category.subCategory ||
      !Array.isArray(category.subCategory)
    ) {
      return [];
    }

    return category.subCategory;
  };

  // Helper function to update subcategories when a category is selected
  const updateSubcategoriesForCategory = (selectedCategory) => {
    if (!selectedCategory) return;

    // Get approved subcategories
    const approvedSubcategories = getApprovedSubcategories(selectedCategory);

    // Update the subcategory filter
    setFilterData((prevFilters) => {
      return prevFilters.map((filter) => {
        if (filter.id === "subcategory") {
          return {
            ...filter,
            options: approvedSubcategories,
            defaultValue: [],
          };
        }
        return filter;
      });
    });

    // Reset subcategory selections
    setSelections((prev) => ({
      ...prev,
      subcategory: [], // Initialize as empty array for multi-select
    }));
  };

  // Use effect to initialize subcategories when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && selections.category) {
      const selectedCategory = categories.find(
        (cat) => cat._id === selections.category
      );
      if (selectedCategory) {
        updateSubcategoriesForCategory(selectedCategory);
      }
    }
  }, [categories, selections.category]);

  // Update the handleSelectionChange function
  const handleSelectionChange = (filterId, value) => {
    console.log("handleSelectionChange called");
    console.log("filterData ++>", filterData);
    console.log("filterId ++>", filterId);
    console.log("value ++>", value);
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));

    // If category changes, update subcategories
    if (filterId === "category") {
      // Find the selected category
      const selectedCategory = categories.find((cat) => cat._id === value);
      if (selectedCategory) {
        updateSubcategoriesForCategory(selectedCategory);
      }
    }
  };

  // Update the handleReset function
  const handleReset = () => {
    handleCancel();
    const initialState = createInitialState(filterData);
    setSelections(initialState);

    // If we have categories, reset subcategories for the first category
    if (categories.length > 0) {
      updateSubcategoriesForCategory(categories[0]);
    }
    // Reset filter params to trigger new API call
    setFilterParams({
      category: null,
      subCategory: [], // Reset to empty array
      status: null,
      duration: null,
      offerType: null,
      redemptionMethod: null,
    });
  };

  // When applying filters, you can now access both category ID and subcategory IDs array
  const handleApply = () => {
    handleCancel();
    console.log("Applying filters:", selections);

    // Create a new object for mapped filter parameters
    const mappedFilterParams = {};

    filterData.forEach((filter) => {
      const selectedValue = selections[filter.id];
      if (
        selectedValue !== null &&
        selectedValue !== undefined &&
        selectedValue !== "" &&
        !(Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        if (filter.id === "category") {
          mappedFilterParams[filter.id] = selectedValue; // Category ID is already correct
        } else if (filter.id === "subcategory") {
          mappedFilterParams[filter.id] = selectedValue; // Subcategory IDs array is correct
        } else if (filter.valueMap) {
          // Use the value map for other filters
          mappedFilterParams[filter.id] = filter.multiSelect
            ? selectedValue.map((val) => filter.valueMap[val]) // For multi-select, map each value
            : filter.valueMap[selectedValue]; // For single select, map the value
        } else {
          mappedFilterParams[filter.id] = selectedValue;
        }
      }
    });
    console.log("Mapped Filter Params:", mappedFilterParams);
    setFilterParams(mappedFilterParams);
    setOpen(false);
  };

  // Handle cancel button click
  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  useEffect(() => {
    if (open) {
      document.body.classList.add("dropdown-open");
    } else {
      document.body.classList.remove("dropdown-open");
    }

    return () => {
      document.body.classList.remove("dropdown-open");
    };
  }, [open]);

  const items = [
    {
      title: "Offers",
    },
    {
      title: "Offers List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Title",
    },
    {
      name: "Category Type",
    },
    {
      name: "Offer Type",
    },
    {
      name: "Discount Type",
    },
    {
      name: "Status",
    },
    {
      name: "Actions",
    },
  ];

  // Make getOffersData a useCallback hook to prevent unnecessary re-creations
  const getOffersData = useCallback(async (search = searchQuery) => { // Default to searchQuery state
    const params = {
      page: currentPage,
      search: search || null, // Use the 'search' argument here
      category: filterParams.category || undefined,
      subcategory:
        filterParams.subcategory && filterParams.subcategory.length > 0
          ? filterParams.subcategory.join(",")
          : undefined,
      offerType: filterParams.offerType || undefined,
      status: filterParams.status || undefined,
      duration: filterParams.duration || undefined,
      redemptionMethod: filterParams.redemptionMethod || undefined,
      limit: 50,
    };

    Object.keys(params).forEach(
      (key) => (params[key] === undefined ? delete params[key] : null)
    );
    console.log("Fetching offers with params:", params);

    try {
      const offersPromise = await dispatch(getNonDraftedOffers(params))?.unwrap();
      if (offersPromise?.success === true) {
        setAllOffers(offersPromise?.data);
        setCurrentPage(offersPromise?.currentPage);
        setTotalPages(offersPromise?.totalPages);
        setCount(offersPromise?.count);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
    }
  }, [currentPage, filterParams, dispatch, searchQuery]); // Add searchQuery to dependencies for direct calls

  // This useEffect will be responsible for initial load and filter changes (excluding search)
  useEffect(() => {
    getOffersData();
  }, [deleteSuccess, currentPage, filterParams, statusToggled, getOffersData]); // Added getOffersData to dependencies

  // Create the debounced function using useCallback
  const debouncedSearchHandler = useCallback(
    debounce((query) => {
      getOffersData(query); // Call getOffersData with the debounced query
    }, 500),
    [getOffersData] // getOffersData is a dependency of debouncedSearchHandler
  );

  // This useEffect will specifically handle the debounced search
  useEffect(() => {
    // Only trigger debounce if searchQuery has changed and is not empty
    if (searchQuery !== null && searchQuery !== "") {
      debouncedSearchHandler(searchQuery);
    } else if (searchQuery === "") {
      // If search is cleared, fetch data without search
      getOffersData("");
    }

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [searchQuery, debouncedSearchHandler, getOffersData]);

  // Update your handleSearchChange to simply set the state
  // const handleSearchChange = (value) => {
  //   setSearchQuery(value);
  // };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of offer list when changing pages
    const container = document.getElementById('offer-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImageIconClick = (offer) => {
    setClickedOffer(offer);
    setIsImageOpen(true);
  };

  const handleCloseImage = () => {
    setIsImageOpen(false);
    setClickedOffer(null);
  };

  const handleLocationClick = (offer) => {
    setClickedOffer(offer);
    setIsLocationOpen(true);
  };

  const handleDescriptionClick = (offer) => {
    setClickedOffer(offer);
    setIsDescriptionOpen(true);
  };

  const handleSubCategoryClick = (offer) => {
    setClickedOffer(offer);
    setIsSubCategoryOpen(true);
  };

  const handleRulesClick = (offer) => {
    setClickedOffer(offer);
    setIsRulesOpen(true);
  };

  const handleCloseLocation = () => {
    setIsLocationOpen(false);
    setClickedOffer(null);
  };

  const handleCloseDescription = () => {
    setIsDescriptionOpen(false);
    setClickedOffer(null);
  };

  const handleCloseSubCategory = () => {
    setIsSubCategoryOpen(false);
    setClickedOffer(null);
  };

  const handleCloseRules = () => {
    setIsRulesOpen(false);
    setClickedOffer(null);
  };

  const handleEditClick = (id) => {
    navigate("/admin/offer-form", {
      state: {
        id: id,
      },
    });
  };

  const handleDeleteClick = (offer) => {
    setDeleteClicked(true);
    setClickedOffer(offer);
  };

  const handleKeepOffer = () => {
    setDeleteClicked(false);
    setClickedOffer(null);
  };

  const handleDeleteOffer = async () => {
    closePopup();
    setDeleteClicked(false);
    if (clickedOffer?._id !== null) {
      const deletePromise = await dispatch(
        deleteOffer({ id: clickedOffer?._id, merchantId: clickedOffer?.merchant?._id })
      )?.unwrap();
      if (deletePromise?.success === true) {
        setDeleteSuccess(!deleteSuccess);
        setClickedOffer(null);
      }
    }
  };

  const handleCloseInfo = () => {
    setInfoClicked(false);
    setClickedOffer(null);
  };

  const handleInfoClick = (offer) => {
    setClickedOffer(offer);
    setInfoClicked(true);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const fetchOfferDetails = async (offerId) => {
    try {
      const offerDetailsPromise = await dispatch(
        getOfferById({ id: offerId })
      )?.unwrap();
      if (offerDetailsPromise?.success === true) {
        setOfferDetails(offerDetailsPromise?.data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const openPopup = (offerId) => {
    fetchOfferDetails(offerId);
  };

  const closePopup = () => {
    setIsDetailOpen(false);
  };

  // Open direct from navigation state (e.g., notifications deep-link)
  useEffect(() => {
    const stateOfferId = location?.state?.openOfferId || location?.state?.offerId;
    if (stateOfferId) {
      openPopup(stateOfferId);
      // Clear state to avoid reopening on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state?.openOfferId, location?.state?.offerId, location.pathname, navigate, openPopup]);

  // Get all offers (now all statuses are selectable)
  const getSelectableOffers = () => {
    return allOffers || [];
  };

  const handleSelectAll = () => {
    const selectableOffers = getSelectableOffers();

    if (
      selectableOffers.length > 0 &&
      selected.length === selectableOffers.length
    ) {
      setSelected([]);
      setSelectedMerchantIds([]);
    } else {
      const newSelected = selectableOffers.map((offer) => offer._id);
      const newSelectedMerchantIds = selectableOffers.map(
        (offer) => offer?.merchant?._id
      );
      setSelected(newSelected);
      setSelectedMerchantIds(newSelectedMerchantIds);
    }
  };

  const handleSelectOne = (event, id, status) => {
    // Allow selection for all statuses now
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    let newSelectedMerchantIds = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
      const offer = allOffers?.find((offer) => offer._id === id);
      const merchantId = offer?.merchant?._id;

      if (merchantId) {
        newSelectedMerchantIds = [...selectedMerchantIds, merchantId];
      } else {
        newSelectedMerchantIds = [...selectedMerchantIds];
      }
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);

      const offer = allOffers?.find((offer) => offer._id === id);
      const merchantId = offer?.merchant?._id;

      if (merchantId) {
        newSelectedMerchantIds = selectedMerchantIds.filter(
          (mId) => mId !== merchantId
        );
      } else {
        newSelectedMerchantIds = [...selectedMerchantIds];
      }
    }

    setSelected(newSelected);
    setSelectedMerchantIds(newSelectedMerchantIds);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Check if offer is selectable (all statuses now)
  const isSelectable = (status) => {
    return true; // Allow selection for all statuses
  };

  const handleBulkApprove = () => {
    setShowApprovalConfirm(true);
  };

  const handleApproveConfirm = async () => {
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "approved",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        setStatusToggled(!statusToggled);
        setShowApprovalConfirm(false);
        setSelected([]);
        setSelectedMerchantIds([]);
      }
    } catch (error) {
      console.error("Error approving offers:", error);
    }
  };

  const handleBulkDecline = () => {
    setShowDeclineConfirm(true);
  };

  // Move selected offers to pending
  const handleBulkPending = async () => {
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "pending",
        reason: "Moved to pending",
        // Provide email bodies to satisfy backend validation
        TextBody: "Your offer status has been changed to pending.",
        HtmlBody: "<p>Your offer status has been changed to <strong>pending</strong>.</p>",
        // Avoid showing warning toast if backend still returns the email-body message
        silent: true,
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        // Optimistic UI: update selected rows to pending immediately
        setAllOffers((prev) =>
          (prev || []).map((o) =>
            selected.includes(o._id) ? { ...o, status: "pending" } : o
          )
        );
        setStatusToggled(!statusToggled);
        setSelected([]);
        setSelectedMerchantIds([]);
        setShowActionModal(false);
        // Also re-fetch in the background to stay in sync
        try { getOffersData(); } catch { }
      }
    } catch (error) {
      console.error("Error setting pending offers:", error);
    }
  };

  const handleDeclineConfirm = async () => {
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "declined",
        reason: "Bulk declined",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        setStatusToggled(!statusToggled);
        setShowDeclineConfirm(false);
        setSelected([]);
        setSelectedMerchantIds([]);
      }
    } catch (error) {
      console.error("Error declining offers:", error);
    }
  };

  const handleDeclineCancel = () => {
    setShowDeclineConfirm(false);
  };
  const handleApproveCancel = () => {
    setShowApprovalConfirm(false);
  };

  const handleSingleApprovalConfirm = async () => {
    if (singleApprovalOfferId) {
      try {
        const payload = {
          type: "offer",
          ids: [singleApprovalOfferId],
          status: "approved",
        };
        const statusPromise = await dispatch(
          approveOrRejectResources(payload)
        )?.unwrap();
        if (statusPromise?.success) {
          setShowSingleApprovalConfirm(false);
          setSingleApprovalOfferId(null);
          setIsDetailOpen(false); // Close the details modal
          setStatusToggled(!statusToggled);
        }
      } catch (error) {
        console.error("Error approving offer:", error);
      }
    }
  };

  const handleSingleApprovalCancel = () => {
    setShowSingleApprovalConfirm(false);
    setSingleApprovalOfferId(null);
  };

  const handleSingleApprovalClick = (offerId) => {
    setIsDetailOpen(false);
    setSingleApprovalOfferId(offerId);
    setShowSingleApprovalConfirm(true);
  };

  useEffect(() => {
    if (selected.length > 0) {
      setShowActionModal(true);
    } else {
      setShowActionModal(false);
    }
  }, [selected]);

  // Calculate count of selectable items for "Select All" checkbox
  const selectableCount = getSelectableOffers().length;
  const allSelectableSelected =
    selectableCount > 0 && selected.length === selectableCount;

  // Determine which bulk action buttons to show based on selected statuses
  const selectedStatuses = selected
    .map((id) => allOffers?.find((o) => o._id === id)?.status)
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const hasPendingSelected = selectedStatuses.includes("pending");
  const showPendingOnly = selected.length > 0 && !hasPendingSelected; // all selected are approved/declined

  console.log("offerDetails", offerDetails);
  const handleApproveClickDirect = async (offerId) => {
    closePopup();
    try {
      // Get the offer details to access merchant ID
      const offer = allOffers?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: "approved",
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: "",
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        setIsDetailOpen(false); // Close the details modal
        getOffersData(); // Refresh the offers data
      }
    } catch (error) {
      console.error("Error approving offer:", error);
    }
  };

  const handleDeclineClick = async (offerId, reason) => {
    closePopup();
    try {
      const offer = allOffers?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: "declined",
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: reason,
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        setIsDetailOpen(false); // Close the details modal
        getOffersData(); // Refresh the offers data
      }
    } catch (error) {
      console.error("Error declining offer:", error);
    }
  };

  // Handle status click to change to pending
  const handleStatusClick = async (offerId, newStatus) => {
    try {
      const offer = allOffers?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: newStatus,
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: `Changed from ${offer.status} to ${newStatus}`,
        ...(newStatus === "pending"
          ? {
            TextBody: `Your offer "${offer?.title || ""}" has been moved back to Pending for review.`,
            HtmlBody: `<p>Your offer <strong>${offer?.title || ""}</strong> has been moved back to <strong>Pending</strong> for review.</p>`,
          }
          : {}),
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        getOffersData(); // Refresh the offers data
      }
    } catch (error) {
      console.error("Error changing offer status:", error);
    }
  };

  return (
    <>
      {(loading === true || allOffers === null) && <Loader />}
      {clickedOffer?.thumbnailImage !== null && isImageOpen === true && (
        <ImagePopup
          image={clickedOffer?.thumbnailImage}
          handleCloseImage={handleCloseImage}
        />
      )}
      {isLocationOpen === true && (
        <LocationPopup
          locations={clickedOffer?.tempLocations}
          handleCloseLocation={handleCloseLocation}
        />
      )}
      {clickedOffer?.description !== "" && isDescriptionOpen === true && (
        <DetailsPopup
          content={clickedOffer?.description}
          handleClose={handleCloseDescription}
          icon={descriptionIcon}
          popupClassName={"w-[335px] sm:w-[470px] pb-5"}
        />
      )}
      {clickedOffer?.subCategory?.length > 0 && isSubCategoryOpen === true && (
        <SubCategoryPopup
          subCategories={clickedOffer?.subCategory}
          handleCloseSubCategory={handleCloseSubCategory}
          icon={<SubCategoryIcon className="w-[10%]" />}
          heading="Subcategories"
        />
      )}
      {clickedOffer?.ruleAndRegulation?.length > 0 && isRulesOpen === true && (
        <SubCategoryPopup
          subCategories={clickedOffer?.ruleAndRegulation}
          handleCloseSubCategory={handleCloseRules}
          icon={<RulesIcon className="w-[10%]" />}
          heading="Rules and Regulations"
        />
      )}

      {/* Updated PopupContent with dynamic redemption codes */}
      {isDetailOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PopupContent
            offerDetails={offerDetails}
            handleDeleteClick={handleDeleteClick}
            handleEditClick={handleEditClick}
            handleSingleApprovalClick={handleApproveClickDirect}
            handleDeclineClick={handleDeclineClick}
            onClose={closePopup}
          />
        </div>
      )}

      {showApprovalConfirm && (
        <CancelPopup
          handleNo={handleApproveCancel}
          handleYes={handleApproveConfirm}
          heading="Approve Offers"
          content="Do you really want to approve all selected offers?"
          confirmation={true}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      {showDeclineConfirm && (
        <CancelPopup
          handleNo={handleDeclineCancel}
          handleYes={handleDeclineConfirm}
          heading="Decline Offers"
          content="Do you really want to decline all selected offers?"
          confirmation={false}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      {showSingleApprovalConfirm && (
        <CancelPopup
          handleNo={handleSingleApprovalCancel}
          handleYes={handleSingleApprovalConfirm}
          heading="Approve Offer"
          content="Do you really want to approve this offer?"
          confirmation={true}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}

      <div className="flex justify-between col-span-12">
        <BreadCrumsHeader heading="Offers List" items={items} />
        {/* <CreateNewButton
          text={"Create New Offer"}
          navigateTo={"/merchant/offer-form"}
          iconClassName="sm:left-[1%] md:left-[8%] bottom-[30%]"
        /> */}
      </div>
      {deleteClicked === true ? (
        <CancelPopup
          handleNo={handleKeepOffer}
          handleYes={handleDeleteOffer}
          heading={"Delete Offer"}
          content={"Are you sure you want to delete this offer?"}
          image={deleteImg}
        />
      ) : null}
      {infoClicked === true && (
        <DetailsPopup
          content={clickedOffer?.reason}
          handleClose={handleCloseInfo}
          icon={infoIcon}
          popupClassName={"w-[335px] sm:w-[470px] pb-5"}
        />
      )}
      <div className="w-full flex justify-between items-center mt-4">
        <SearchBar
          parentDivClass="w-[65%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative"
          inputClassName="searchBar w-full p-3 text-[14px] font-poppins font-[400] outline-none"
          iconClassName="absolute sm:right-[5%] sm:bottom-[25%] lg:right-[3%]"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
        <Dropdown
          overlay={
            <FilterPanel
              filterData={filterData}
              selections={selections}
              handleApply={handleApply}
              handleCancel={handleCancel}
              handleReset={handleReset}
              handleSelectionChange={handleSelectionChange}
            />
          }
          trigger={["click"]}
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomRight"
          overlayStyle={{
            minWidth: "320px",
            maxHeight: "80vh",
            zIndex: 1000,
          }}
          overlayClassName="filter-dropdown-overlay"
        >
          <button className="flex justify-center items-center gap-2 border border-[#00ADEF] rounded-md p-3 text-lg font-medium outline-none h-[40px]">
            <div className="flex items-center gap-2">
              <FiltersIcon className="w-[20px] h-[20px]" />
              <span className="font-[500] text-[14px] font-poppins leading-[22px]">
                Filters
              </span>
            </div>
            <DownArrow />
          </button>
        </Dropdown>
      </div>
      <div id="offer-list-container" className="col-span-12 mt-4">
        <TableContainer style={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: "800px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                <TableCell className="!border-b-0">
                  <Checkbox
                    checked={allSelectableSelected}
                    onChecked={handleSelectAll}
                    divClassName="flex items-center"
                    UncheckedIcon={UncheckedIcon}
                    checkClassName={"!w-[22px] !h-[22px]"}
                  />
                </TableCell>
                {columns.map((column) => {
                  return (
                    <TableCell key={column.name} className="!text-white !font-semibold" style={{ color: 'white !important' }}>
                      {column.name}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allOffers && allOffers.map((offer, index) => {
                const isItemSelected = isSelected(offer._id);
                const itemSelectable = isSelectable(offer.status);
                return (
                  <TableRow
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                    key={offer._id}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isItemSelected}
                        onChecked={(event) =>
                          handleSelectOne(event, offer._id, offer.status)
                        }
                        divClassName="flex items-center"
                        checkClassName="!w-[22px] !h-[22px]"
                      />
                    </TableCell>
                    <TableCell className="max-w-[50px]">{(currentPage - 1) * 50 + index + 1}</TableCell>
                    <TableCell
                      title={offer?.title}
                      className="max-w-[100px] truncate"
                    >
                      {offer?.title || "N/A"}
                    </TableCell>
                    <TableCell
                      title={offer?.category?.title}
                      className="max-w-[80px] truncate"
                    >
                      {offer?.category?.title || "N/A"}
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      {(() => {
                        switch (offer?.offerType) {
                          case "ONLINE_OFFER":
                            return "ONLINE OFFER";
                          case "INSTORE_SWOODLE_OFFER":
                            return "INSTORE SWOODLE OFFER";
                          default:
                            return "N/A";
                        }
                      })()}
                    </TableCell>
                    <TableCell
                      title={offer?.offerDiscountType}
                      className="max-w-[80px] truncate"
                    >
                      {(() => {
                        switch (offer?.offerDiscountType) {
                          case "FREE_OFFER":
                            return "FREE OFFER";
                          case "DISCOUNTED_PERCENT":
                            return "DISCOUNTED PERCENT";
                          case "FIXED_DOLLAR":
                            return "FIXED DOLLAR";
                          case "BUY_ONE_GET_ONE_FREE":
                            return "BUY ONE GET ONE FREE";
                          case "BUNDLE_OFFER":
                            return "BUNDLE OFFER";
                          default:
                            return "N/A";
                        }
                      })()}
                    </TableCell>
                    <TableCell className="max-w-[110px]">
                      <Confirmation
                        status={offer?.status}
                        onStatusClick={handleStatusClick}
                        offerId={offer?._id}
                        enablePendingAction={true}
                        handleInfoClick={() => handleInfoClick(offer)}
                      />
                    </TableCell>
                    {/* <TableCell>
                      <Status
                        isEnabled={offer?.isEnabled}
                        handleToggle={() => handleToggle(offer?._id)}
                      />
                    </TableCell> */}
                    <TableCell className="max-w-[120px]">
                      <button
                        className="flex justify-center items-center border border-[#00ADEF] rounded-[6px] p-3 h-[34px]"
                        onClick={() => openPopup(offer?._id)}
                      >
                        <span className="font-[600] text-[14px] text-[#00ADEF] font-poppins leading-[22px]">
                          View Details
                        </span>
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />

        {/* Action Modal */}
        {showActionModal && (
          <div
            className="fixed bottom-16 left-[57%] transform -translate-x-1/2 bg-white rounded-[6px] px-4 h-[60px] flex items-center gap-4 z-50 text-[14px] font-[600] text-[#303030] border border-[#0000001A]"
            style={{ boxShadow: "0px 4px 20px 0px #00000029" }}
          >
            <div className="flex items-center gap-2">
              <span className="!text-[12px] !font-[500] !text-[#000000] border border-[#2A35484D] rounded-[4px] w-[24px] h-[24px] flex items-center justify-center">
                {selected.length}
              </span>
              <span className="!font-[500]">Selected</span>
            </div>
            <div className="flex items-center gap-16">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={allSelectableSelected}
                    onChecked={handleSelectAll}
                    divClassName="flex items-center"
                    UncheckedIcon={UncheckedGreyIcon}
                    checkClassName={"!w-[22px] !h-[22px]"}
                  />
                  <span className="whitespace-nowrap !font-[500]">
                    Select All
                  </span>
                </div>
                {showPendingOnly ? (
                  <button
                    onClick={handleBulkPending}
                    className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap px-4 py-1 rounded-[6px]"
                  >
                    Pending Offers
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleBulkApprove}
                      className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap pl-4 pr-8 py-1 rounded-[6px] flex items-center gap-2"
                    >
                      <img
                        src={whiteTick}
                        alt="tick"
                        className="h-[16px] w-[16px]"
                      />
                      Approve Offers
                    </button>
                    <button
                      onClick={handleBulkDecline}
                      className="border border-[#00ADEF] text-[#00ADEF] h-[38px] px-4 py-1 rounded-[6px] whitespace-nowrap"
                    >
                      Decline Offers
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() => {
                  setSelected([]);
                  setSelectedMerchantIds([]);
                }}
                className="w-[10px] h-[10px]"
              >
                <img src={closeIcon} alt="Close" className="w-full h-full" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminOfferlist;